import { Capacitor } from '@capacitor/core';
import { isRevenueCatConfigured } from '@/config/revenuecat';

/**
 * Which store, if any, may sell or manage the subscription on this device.
 *
 * Google Play's Payments policy requires digital subscriptions sold inside a
 * Play-distributed app to go through Play Billing, and its anti-steering
 * rules forbid pointing users at an external payment flow instead — so
 * Stripe can never be offered on native Android. Once RevenueCat is
 * configured (Play Console + RevenueCat linked, a real API key set — see
 * PLAY_BILLING_PLAN.md), Android gets 'play' instead. Until then it gets
 * null: no purchase path at all, not even a broken one.
 *
 * 'stripe' also covers "Manage Subscription" — Stripe's billing portal is an
 * external payment surface even though opening it isn't itself a purchase,
 * so it's gated the same way. Play's own subscription-management page is
 * the platform's native surface, not steering, so 'play' callers may link to
 * it (see utils/playBilling.ts openManageSubscription).
 *
 * A native iOS build will need its own case here for App Store guideline
 * 3.1.1 — deliberately checked by platform, not by a single "is native" flag.
 */
export type PurchaseProvider = 'stripe' | 'play' | null;

export function getPurchaseProvider(): PurchaseProvider {
  let platform: string;
  try {
    platform = Capacitor.getPlatform();
  } catch {
    return null;
  }
  if (platform === 'web') return 'stripe';
  if (platform === 'android') return isRevenueCatConfigured() ? 'play' : null;
  return null; // iOS: not yet built
}

/** Whether any purchase path exists at all, for callers that only need to
 * decide between explaining a limit and offering a way past it — not which
 * store to use. See getPurchaseProvider() for that. */
export function canPurchaseInApp(): boolean {
  return getPurchaseProvider() !== null;
}
